export interface SessionUser {
  user_id: string;
  tenant_id: string;
  email: string;
  role: string;
}

export async function fetchSession(): Promise<SessionUser | null> {
  try {
    const res = await fetch("/dashboard/auth/session", { credentials: "include" });
    if (!res.ok) return null;
    return (await res.json()) as SessionUser;
  } catch {
    return null;
  }
}

export async function login(email: string, password: string): Promise<SessionUser> {
  const res = await fetch("/dashboard/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Login failed" }));
    throw new Error(body.detail || "Login failed");
  }
  return (await res.json()) as SessionUser;
}

// ── CSRF token — fetched from the server, cached in memory ───────────────
// The token lives in the Redis session and is never stored in a cookie.
// Clients fetch it once per session via GET /dashboard/auth/csrf and cache
// it here. Cleared on logout so the next login fetches a fresh token.

let _csrfToken: string | null = null;

export async function fetchCsrfToken(): Promise<string> {
  if (_csrfToken) return _csrfToken;
  try {
    const res = await fetch("/dashboard/auth/csrf", { credentials: "include" });
    if (!res.ok) return "";
    const data = await res.json();
    const token = typeof data?.csrf_token === "string" ? data.csrf_token : "";
    _csrfToken = token;
    return token;
  } catch {
    return "";
  }
}

export function clearCsrfCache(): void {
  _csrfToken = null;
}

export async function logout(): Promise<void> {
  const token = await fetchCsrfToken();
  clearCsrfCache();
  await fetch("/dashboard/auth/logout", {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRF-Token": token },
  }).catch(() => {});
}

// ── Invite acceptance & password reset (pre-login, token-credentialed) ────

async function postJson<T>(url: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({ detail: fallback }));
    // FastAPI validation errors carry a list, not a sentence.
    throw new Error(typeof data.detail === "string" ? data.detail : fallback);
  }
  return (await res.json()) as T;
}

export async function lookupInvite(token: string): Promise<{ email: string }> {
  return postJson("/dashboard/auth/invite/lookup", { token }, "Invalid or expired link");
}

export async function acceptInvite(token: string, password: string): Promise<SessionUser> {
  return postJson("/dashboard/auth/invite/accept", { token, password }, "Invalid or expired link");
}

/** Admin handoff into an internal tenant's dashboard (see the admin pane's
 * "View their dashboard"): single-use token, no password step. */
export async function redeemHandoff(token: string): Promise<SessionUser> {
  return postJson("/dashboard/auth/handoff", { token }, "Invalid or expired link");
}

export async function requestPasswordReset(email: string): Promise<void> {
  await postJson("/dashboard/auth/password-reset/request", { email }, "Request failed");
}

export async function confirmPasswordReset(token: string, password: string): Promise<SessionUser> {
  return postJson(
    "/dashboard/auth/password-reset/confirm",
    { token, password },
    "Invalid or expired link",
  );
}

// ── Self-serve signup (email): start → verify code → finish ───────────────

export interface SignupStatus {
  mode: "closed" | "invite_only" | "open";
  signup_open: boolean;
  invite_valid: boolean | null;
  /** Public key for the Cloudflare Turnstile widget on the Signup page. */
  turnstile_site_key?: string;
  /** The "Continue with" providers this deployment has set up, e.g. ["github"]. */
  sign_in_providers?: string[];
}

export async function getSignupStatus(invite?: string): Promise<SignupStatus> {
  const query = invite ? `?invite=${encodeURIComponent(invite)}` : "";
  try {
    const res = await fetch(`/dashboard/auth/signup/status${query}`, { credentials: "include" });
    if (!res.ok) throw new Error("status unavailable");
    return (await res.json()) as SignupStatus;
  } catch {
    // Unknown is treated as closed: the page never offers a signup it can't honour.
    return { mode: "closed", signup_open: false, invite_valid: null };
  }
}

export async function startSignup(
  email: string,
  password: string,
  invite: string | undefined,
  turnstileToken: string,
): Promise<void> {
  await postJson(
    "/dashboard/auth/signup/start",
    { email, password, invite: invite || null, turnstile_token: turnstileToken },
    "Signup failed",
  );
}

export async function verifySignupCode(email: string, code: string): Promise<{ signup_token: string }> {
  return postJson("/dashboard/auth/signup/verify", { email, code }, "That code didn't work");
}

export async function resendSignupCode(email: string): Promise<void> {
  await postJson("/dashboard/auth/signup/resend", { email }, "Couldn't send a new code");
}

/** ``signupToken`` from verify for an email signup; null for a GitHub signup,
 * whose pending signup rides in a cookie. */
export async function completeSignup(
  signupToken: string | null,
  companyName: string,
  acceptTerms: boolean,
  priority?: PriorityChoice,
): Promise<SessionUser> {
  return postJson(
    "/dashboard/auth/signup/complete",
    {
      signup_token: signupToken,
      company_name: companyName,
      accept_terms: acceptTerms,
      ...(priority && {
        data_tier: priority.dataTier,
        accept_priority_addendum: priority.acceptPriorityAddendum,
        accept_collaborative_consent: priority.acceptCollaborativeConsent,
      }),
    },
    "Couldn't finish signing up",
  );
}

export type DataTier = "privacy" | "collaborative";

/** What a Priority signup (ADR-0004) chose at Finish signup. */
export interface PriorityChoice {
  dataTier: DataTier;
  acceptPriorityAddendum: boolean;
  acceptCollaborativeConsent: boolean;
}

/** What Finish signup offers: a Priority Benchmark when Phantm approved the
 * signup's proven address, else nothing extra. Throws when the signup has
 * expired. */
export async function getSignupOffer(signupToken: string | null): Promise<{ priority: { benchmark_days: number } | null }> {
  return postJson("/dashboard/auth/signup/offer", { signup_token: signupToken }, "This signup has expired");
}

// ── Signing in and up with GitHub or Google ────────────────────────────────

/** The "Continue with" providers, in the order their buttons appear. */
export const PROVIDER_LABELS = new Map([
  ["github", "GitHub"],
  ["google", "Google"],
]);

/** Sends the browser to another site (a provider's sign-in page). */
export function leaveFor(url: string): void {
  window.location.assign(url);
}

/** Where "Continue with GitHub" (or Google) goes: the server sends the
 * browser on to the provider, and it comes back to /login, /signup/finish or
 * the dashboard. */
export function oauthStartUrl(provider: string, origin: "login" | "signup", invite?: string): string {
  const params = new URLSearchParams({ origin });
  if (invite) params.set("invite", invite);
  return `/dashboard/auth/oauth/${provider}/start?${params}`;
}

export interface PendingSignup {
  email: string;
  provider: string;
  company_hint: string | null;
}

/** The GitHub or Google signup waiting for Finish signup, or null when it has
 * expired. */
export async function getPendingSignup(): Promise<PendingSignup | null> {
  try {
    const res = await fetch("/dashboard/auth/signup/pending", { credentials: "include" });
    return res.ok ? ((await res.json()) as PendingSignup) : null;
  } catch {
    return null;
  }
}

const SIGN_IN_ERRORS: Record<string, (provider: string) => string> = {
  cancelled: (p) => `Sign-in with ${p} was cancelled.`,
  state_mismatch: () => "That sign-in didn't start in this browser. Please try again.",
  expired: () => "That sign-in took too long or was already used. Please try again.",
  too_many_attempts: () => "Too many sign-in attempts from your network. Try again in a few minutes.",
  provider_unavailable: (p) => `Sign-in with ${p} isn't available right now. Use your email instead.`,
  provider_error: (p) => `${p} didn't confirm the sign-in. Please try again.`,
  email_unverified: (p) =>
    `Your ${p} account has no verified primary email. Add one on ${p}, or use another way to sign in.`,
  member_email_unverified: (p) =>
    `An account with this email already exists. Sign in with your password first, then connect ${p} from Settings.`,
  account_disabled: () => "This account has been deactivated. Ask an admin on your team.",
  account_suspended: () => "Phantm has suspended your company's access. Contact support@getphantm.ai.",
  disposable_email: () => "Please use a permanent email address, not a disposable one.",
  signup_closed: () => "No Phantm account uses this email, and signups are closed right now.",
  invite_invalid: () => "This signup link is invalid, used or expired. Ask Phantm for a new one.",
  // Connecting from Settings.
  identity_in_use: (p) => `That ${p} account is already connected to another Phantm login.`,
  already_connected: (p) => `You already have a ${p} account connected. Disconnect it first to connect another.`,
};

/** What the Login, Signup and Settings pages say when GitHub or Google sends
 * the person back with ``?error=<code>&via=<provider>``; "" when there is no
 * error. */
export function signInErrorMessage(code: string | null, via: string | null): string {
  if (!code) return "";
  // Google has no "primary email" to add: the account's one address is it.
  if (code === "email_unverified" && via === "google") {
    return "Google hasn't verified this account's email address. Use another way to sign in.";
  }
  const provider = PROVIDER_LABELS.get(via ?? "") ?? "that provider";
  const message = Object.prototype.hasOwnProperty.call(SIGN_IN_ERRORS, code) ? SIGN_IN_ERRORS[code] : undefined;
  return message ? message(provider) : "Sign-in didn't work. Please try again.";
}
